import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import LogoutIcon from '@mui/icons-material/Logout';
import MicIcon from '@mui/icons-material/Mic';
import { t } from 'i18next';
import { SupervisorSession } from '../hooks/useSupervisorSession';

interface SupervisorBarProps {
  session: SupervisorSession;
  /** Joining a call that is not live has nothing to join; the API refuses it too. */
  canJoin: boolean;
}

/** The popup is a fixed 450px; without this one long error puts a scrollbar on the dialog. */
const alertSx = {
  mt: 1,
  maxWidth: '100%',
  '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' },
} as const;

const SignalStatus: React.FC<{ session: SupervisorSession }> = ({ session }) => {
  switch (session.signal) {
    case 'awaiting-ack':
      return (
        <Chip
          size="small"
          variant="outlined"
          icon={<CircularProgress size={12} />}
          label={t('supervisor.signalWaiting')}
        />
      );
    case 'acked':
      return (
        <Chip
          size="small"
          color="success"
          icon={<CheckCircleIcon />}
          label={t('supervisor.signalAcked')}
        />
      );
    case 'timed-out':
      return (
        <Chip
          size="small"
          color="error"
          variant="outlined"
          icon={<ErrorOutlineIcon />}
          label={t('supervisor.signalTimedOut')}
        />
      );
    default:
      return null;
  }
};

export const SupervisorBar: React.FC<SupervisorBarProps> = ({ session, canJoin }) => {
  const {
    connection,
    connectionError,
    isJoined,
    signal,
    takeOver,
    isMicEnabled,
    join,
    leave,
    sendSignal,
    requestTakeOver,
    forceTakeOver,
    releaseTakeOver,
  } = session;

  const isJoining = connection === 'joining';
  const isAwaitingAgent = takeOver === 'awaiting-agent' || takeOver === 'requesting';
  const isLive = takeOver === 'active' || takeOver === 'unconfirmed';

  return (
    <Box
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        border: '1px solid',
        borderColor: isLive ? 'error.main' : 'divider',
        borderRadius: 1,
        p: 1.5,
        mb: 1,
        backgroundColor: isLive ? 'error.lighter' : 'background.paper',
      }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        {!isJoined ? (
          <Button
            variant="contained"
            size="small"
            startIcon={isJoining ? <CircularProgress size={14} /> : <HeadsetMicIcon />}
            disabled={!canJoin || isJoining}
            onClick={join}>
            {isJoining ? t('supervisor.joining') : t('supervisor.join')}
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={leave}>
              {t('supervisor.leave')}
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<CampaignIcon />}
              disabled={signal === 'sending' || signal === 'awaiting-ack'}
              onClick={sendSignal}>
              {signal === 'sending' ? t('supervisor.signalSending') : t('supervisor.signal')}
            </Button>

            {!isLive ? (
              <Button
                variant="contained"
                color="warning"
                size="small"
                startIcon={isAwaitingAgent ? <CircularProgress size={14} /> : <MicIcon />}
                disabled={isAwaitingAgent}
                onClick={requestTakeOver}>
                {takeOver === 'requesting'
                  ? t('supervisor.takeOverRequesting')
                  : t('supervisor.takeOver')}
              </Button>
            ) : (
              <Button variant="contained" color="error" size="small" onClick={releaseTakeOver}>
                {t('supervisor.handBack')}
              </Button>
            )}
          </>
        )}

        <SignalStatus session={session} />
      </Stack>

      {isJoined && !isLive && !isAwaitingAgent && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {t('supervisor.listening')}
        </Typography>
      )}

      {!isJoined && !canJoin && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {t('supervisor.joinBlocked')}
        </Typography>
      )}

      {!isJoined && canJoin && connection !== 'error' && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {t('supervisor.joinHint')}
        </Typography>
      )}

      {takeOver === 'awaiting-agent' && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {t('supervisor.takeOverWaiting')}
        </Typography>
      )}

      {takeOver === 'timed-out' && (
        <Alert
          severity="warning"
          sx={alertSx}
          action={
            <Button color="inherit" size="small" onClick={forceTakeOver}>
              {t('supervisor.takeOverForce')}
            </Button>
          }>
          {t('supervisor.takeOverTimedOut')} {t('supervisor.takeOverForceHint')}
        </Alert>
      )}

      {takeOver === 'error' && (
        <Alert severity="error" sx={alertSx}>
          {t('supervisor.takeOverFailed')}
          {connectionError ? `: ${connectionError}` : ''}
        </Alert>
      )}

      {connection === 'error' && connectionError && (
        <Alert severity="error" sx={alertSx}>
          {connectionError}
        </Alert>
      )}

      {isMicEnabled && (
        <Alert severity={takeOver === 'unconfirmed' ? 'warning' : 'error'} sx={alertSx}>
          {takeOver === 'unconfirmed'
            ? t('supervisor.liveUnconfirmedWarning')
            : t('supervisor.liveWarning')}
        </Alert>
      )}
    </Box>
  );
};
